import {
  formatE164,
  formatInternational,
  getCountryCodeFromPhoneNumber,
  getPhoneNumberCountryCodeFormatted,
  getPhoneNumberNationalNumber,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  validatePossibleNumberWithReason,
  ValidationResult,
} from 'lib-shared-phone-numbers';

function getPhoneNumberStringFromPayload(payload: {
  phoneNumber: string;
}): string {
  const { phoneNumber } = payload;
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return '';
  }
  return phoneNumber;
}

/**
 * [isNumberValid]
 *
 * @param {[type]} payload {string: a phone number in e164 format}
 * @returns {Boolean}
 */
export function isNumberValid(payload: { phoneNumber: string }): boolean {
  const phoneNumber = getPhoneNumberStringFromPayload(payload);

  if (phoneNumber) {
    return (
      isValidPhoneNumber(phoneNumber) && isPossiblePhoneNumber(phoneNumber)
    );
  }
  return false;
}
/**
 * [isNumberPossible]
 *
 * @param {[type]} payload {string: a phone number in e164 format}
 * @returns {Boolean}
 */
export function isNumberPossible(payload: { phoneNumber: string }): boolean {
  const phoneNumber = getPhoneNumberStringFromPayload(payload);

  if (phoneNumber) {
    return isPossiblePhoneNumber(phoneNumber);
  }
  return false;
}

/**
 * [isNumberValid]
 *
 * @param {[type]} payload {string: a phone number in e164 format}
 * @returns {Boolean | String} Return true if valid else string with reason.
 */
export function isNumberValidWithReason(payload: {
  phoneNumber: string;
}): true | string {
  const phoneNumber = getPhoneNumberStringFromPayload(payload);
  const defaultError = `Invalid number. Did you remember country code with "+" ?`;
  if (phoneNumber) {
    const errorReason = validatePossibleNumberWithReason(phoneNumber);
    if (errorReason === ValidationResult.IS_POSSIBLE) {
      return true;
    }

    switch (errorReason) {
      case ValidationResult.INVALID_COUNTRY_CODE:
        return `Invalid country code`;
      case ValidationResult.TOO_SHORT:
        return `Too short`;
      case ValidationResult.TOO_LONG:
        return `Too long`;
    }

    return defaultError;
  }
  return defaultError;
}

/**
 * [description]
 *
 * @param {[type]} payload {string: a phone number in e164 format}
 * @returns {String}
 */
export function numberFormatterInternational(payload: {
  phoneNumber: string;
}): string {
  const phoneNumber = getPhoneNumberStringFromPayload(payload);
  if (!phoneNumber) return '';
  return formatInternational(phoneNumber) || phoneNumber;
}

/**
 * [description]
 *
 * @param {[type]} payload {string: a phone number in e164 format}
 * @returns {String}
 */
export function numberFormatterE164(payload: { phoneNumber: string }): string {
  const phoneNumber = getPhoneNumberStringFromPayload(payload);
  if (!phoneNumber) return '';
  return formatE164(phoneNumber) || phoneNumber;
}

export type MicrophoneResponse = {
  error: boolean;
  message: string;
  name: string | null;
};

export async function testMicrophonePermissionWithReason(): Promise<void> {
  // MediaDevices Permission test.
  await window.navigator.mediaDevices
    .getUserMedia({ audio: true, video: false })
    .then((mediaStream) => {
      const track = mediaStream.getTracks()[0];
      track.stop(); // We stop the testing media stream.
      return Promise.resolve();
    })
    .catch((error: { name: string }) => {
      const microphone: MicrophoneResponse = {
        error: false,
        message: '',
        name: null,
      };
      console.error(error, error.name);
      if (error.name === 'PermissionDeniedError') {
        microphone.message =
          'Permissions have not been granted to use your microphone, you need to allow access to your microphone in order to receive and make phone calls. (' +
          error.name +
          ').';
      } else if (error.name === 'NotFoundError') {
        microphone.message =
          'No microphone found you need a microphone in order to receive and make phone calls. (' +
          error.name +
          ').';
      } else if (error.name === 'AbortError') {
        microphone.message =
          'Although permissions is granted by both the user and operating system to access the microphone, and no hardware issues occurred that would throw the NotReadableError exception, some problem occurred which prevented the microphone from being used. You need a working microphone in order to receive and make phone calls. (' +
          error.name +
          ').';
      } else if (error.name === 'NotAllowedError') {
        microphone.message =
          'Permissions have not been granted to use your microphone, you need to allow access to your microphone in order to receive and make phone calls. (' +
          error.name +
          ').';
      } else if (
        error.name === 'NotReadableError' ||
        error.name === 'OverConstrainedError'
      ) {
        microphone.message =
          'A hardware error occurred at the operating system or browser level which prevented access to the microphone. You need a working microphone in order to receive and make phone calls. (' +
          error.name +
          ').';
      } else if (error.name === 'SecurityError') {
        microphone.message =
          'You need a working microphone in order to receive and make phone calls. (' +
          error.name +
          ').';
      }
      microphone.name = error.name;
      microphone.error = true;
      return Promise.reject(microphone);
    });
}

export const containsNumber = (string: string): boolean => /\d/.test(string);

export const getPhoneNumberDetails = (
  phoneNumberString: string,
): {
  countryCode: string;
  countryISO: string;
  nationalNumber: number | undefined;
} => {
  const countryCode =
    getPhoneNumberCountryCodeFormatted(phoneNumberString) || '';
  const countryISO = getCountryCodeFromPhoneNumber(phoneNumberString) || '';
  const nationalNumber = getPhoneNumberNationalNumber(phoneNumberString);
  return { countryCode, countryISO, nationalNumber };
};
